// src/lib/supabase.js
// Cliente Supabase + funciones helper equivalentes a firestore.js
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ============================================
// HELPER: Realtime subscription wrapper
// ============================================
/**
 * Subscribe to a table with optional filters.
 * Returns an unsubscribe function (like Firestore onSnapshot).
 *
 * Usage:
 *   const unsub = subscribeToTable('users', { column: 'status', value: 'active' }, (rows) => { ... });
 *   // later: unsub();
 */
export const subscribeToTable = (table, filter, callback, orderByCol = 'created_at', orderAsc = false) => {
  // Initial fetch
  const fetchData = async () => {
    let query = supabase.from(table).select('*');
    if (filter) {
      if (filter.op === 'contains') {
        query = query.contains(filter.column, filter.value);
      } else {
        query = query.eq(filter.column, filter.value);
      }
    }
    if (orderByCol) {
      query = query.order(orderByCol, { ascending: orderAsc });
    }
    const { data, error } = await query;
    if (!error) callback(data || []);
  };
  fetchData();

  // Realtime subscription
  const channelName = `${table}-${filter ? `${filter.column}-${filter.value}` : 'all'}-${Date.now()}`;
  const channel = supabase
    .channel(channelName)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: table,
      ...(filter && !filter.op ? { filter: `${filter.column}=eq.${filter.value}` } : {}),
    }, () => {
      // On any change, refetch
      fetchData();
    })
    .subscribe();

  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel);
  };
};

// ============================================
// GENERIC CRUD HELPERS
// ============================================

export const insertRow = async (table, data) => {
  const { data: result, error } = await supabase
    .from(table)
    .insert(data)
    .select()
    .single();
  if (error) throw error;
  return result;
};

export const updateRow = async (table, id, updates) => {
  const { data, error } = await supabase
    .from(table)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updateRowByFirebaseId = async (table, firebaseId, updates) => {
  const { data, error } = await supabase
    .from(table)
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('firebase_id', firebaseId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteRow = async (table, id) => {
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('id', id);
  if (error) throw error;
};

export const deleteRowByFirebaseId = async (table, firebaseId) => {
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('firebase_id', firebaseId);
  if (error) throw error;
};

export const getRowByFirebaseId = async (table, firebaseId) => {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('firebase_id', firebaseId)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data;
};

export const getRows = async (table, filters = {}, orderBy = 'created_at', ascending = false) => {
  let query = supabase.from(table).select('*');
  for (const [col, val] of Object.entries(filters)) {
    query = query.eq(col, val);
  }
  query = query.order(orderBy, { ascending });
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export default supabase;
